<script setup lang="ts">
type StudentScholarshipData = App.Students.Data.StudentScholarshipData;
type ApplicationData = App.Students.Data.ApplicationData;
type TestData = App.Students.Data.TestData;
type FinancialAidData = App.Students.Data.FinancialAidData;
type DataRequestData = App.DataRequests.Data.DataRequestData;
type PartialStudentScholarshipData = App.Students.Data.PartialStudentScholarshipData;
type PartialApplicationData = App.Students.Data.PartialApplicationData;
type PartialFinancialAidData = App.Students.Data.PartialFinancialAidData;
type PartialTestData = App.Students.Data.PartialTestData;

import CardList from '@/components/studentParentApp/card/CardList.vue';
import EditAlt from '@/icons/line/edit-alt.svg';
import Clock from '@/icons/line/clock.svg';
import IconBadgeSquare from '@/components/badge/IconBadgeSquare.vue';
import Alert from '@/components/alert/Alert.vue';
import TimesCircle from '@/icons/line/times-circle.svg';
import File from '@/icons/line/file.svg';
import University from '@/icons/line/university.svg';
import AwardAlt from '@/icons/line/award-alt.svg';
import UsdCircle from '@/icons/line/usd-circle.svg';
import { formatDate } from '@/utils/date';
import Plus from '@/icons/line/plus.svg';
import { ButtonVariant } from '@/hooks/useButtonClasses';
import IconButton from '@/components/button/IconButton.vue';
import EmptyState from '@/domains/student-app/components/tasks/EmptyState.vue';
import { useDataRequests } from '@/domains/student-app/composables/useDataRequests';
import { RouterLink } from 'hybridly/vue';

const props = defineProps<{
  items: StudentScholarshipData[] | ApplicationData[] | TestData[] | FinancialAidData[];
  activeCreateDataRequests?: DataRequestData[] | null;
  title: string;
  type: 'student_scholarship' | 'application' | 'test' | 'financial_aid';
  hasHeader?: boolean;
  id?: string;
}>();

const { getRoute, getCreateFromRejectedRoute, createRoute } = useDataRequests(props.type);

const computedItems: ComputedRef<
  (StudentScholarshipData | ApplicationData | TestData | FinancialAidData | DataRequestData)[]
> = computed(() => {
  const base = props.activeCreateDataRequests
    ? [
        ...props.activeCreateDataRequests.filter(
          (dataRequest) => dataRequest.requestable_type.value === props.type
        ),
        ...props.items
      ]
    : [...props.items];

  return base.sort((leftItem, rightItem) => {
    const getSortTimestamp = (item) => {
      if ('requestable_type' in item) {
        return new Date(item?.updated_at).getTime();
      }

      const timestamp = item?.pending_data_request?.updated_at ?? item?.updated_at;
      return new Date(timestamp).getTime();
    };

    return getSortTimestamp(rightItem) - getSortTimestamp(leftItem);
  });
});

const icon = computed(() => {
  switch (props.type) {
    case 'student_scholarship':
      return AwardAlt;
    case 'application':
      return University;
    case 'test':
      return File;
    case 'financial_aid':
      return UsdCircle;
  }
});

function getIconColor(
  item: StudentScholarshipData | ApplicationData | TestData | FinancialAidData | DataRequestData
) {
  if ('requestable_type' in item) {
    return getDataRequestIconColor(item);
  }

  if (item.pending_data_request?.id) {
    return 'warning';
  }
  if (item.undismissed_rejected_data_request?.id) {
    return 'danger';
  }
  return 'secondary';
}

function getDataRequestIconColor(item: DataRequestData) {
  if (item.status.value === 'pending') {
    return 'warning';
  }
  if (item.status.value === 'rejected') {
    return 'danger';
  }
  return 'secondary';
}

function getDisplayName(
  item: StudentScholarshipData | ApplicationData | TestData | FinancialAidData | DataRequestData
): string {
  if ('requestable_type' in item && item.type.value === 'create') {
    return getCreateDataRequestDisplayName(item);
  }
  switch (props.type) {
    case 'student_scholarship':
      return (item as StudentScholarshipData).scholarship?.name || '';
    case 'application':
      return (item as ApplicationData).institute?.name || '';
    case 'test':
      return (item as TestData).type.label || '';
    case 'financial_aid':
      return 'FAFSA';
    default:
      return '';
  }
}

function getCreateDataRequestDisplayName(item: DataRequestData): string {
  switch (item.requestable_type.value) {
    case 'student_scholarship':
      return (
        (item.attributes as PartialStudentScholarshipData)?.scholarship_name || 'New Scholarship'
      );
    case 'application':
      return (item.attributes as PartialApplicationData)?.institute?.name || 'New Application';
    case 'test':
      return (item.attributes as PartialTestData)?.type?.label || 'New Test';
    default:
      return '';
  }
}

function getDetail(
  item: StudentScholarshipData | ApplicationData | TestData | FinancialAidData | DataRequestData
) {
  if ('requestable_type' in item) {
    return getDataRequestDetail(item);
  }
  if (props.type === 'financial_aid') {
    return `FAFSA status: ${item['status'].label}`;
  }
  if (item['status']) {
    return `Application status: ${item['status'].label}`;
  }
  if (item['date'] && props.type === 'test') {
    const date = formatDate(item['date']);
    return `Exam date: ${date}`;
  }
  return '';
}

function getDataRequestDetail(item: DataRequestData) {
  if (item.requestable_type.value === 'financial_aid') {
    const status = (item.attributes as PartialFinancialAidData)?.status;
    return status ? `FAFSA status: ${status}` : '';
  }
  if ('attributes' in item && item.attributes && 'status' in item.attributes) {
    const status = (item.attributes as PartialApplicationData | PartialStudentScholarshipData)
      .status;
    return status ? `Application status: ${status?.label}` : '';
  }
  if (item.requestable_type.value === 'test') {
    const date = formatDate((item.attributes as PartialTestData)?.date);
    return `Exam date: ${date}`;
  }
  return '';
}

function isPendingDataRequest(
  item: StudentScholarshipData | ApplicationData | TestData | FinancialAidData | DataRequestData
) {
  if ('requestable_type' in item) {
    return item.status.value === 'pending';
  }
  return item.pending_data_request?.id;
}

function getUndismissedRejectedDataRequest(
  item: StudentScholarshipData | ApplicationData | TestData | FinancialAidData | DataRequestData
): DataRequestData | null {
  if ('requestable_type' in item) {
    return item.status.value === 'rejected' && !item.rejection_dismissed ? item : null;
  }
  return item.undismissed_rejected_data_request ?? null;
}
</script>

<template>
  <div :class="{ relative: id }">
    <div v-if="id" class="absolute -top-16" :id="id"></div>
    <h2
      v-if="title && !hasHeader"
      class="pb-4 text-xs font-bold uppercase leading-3 text-slate-500"
    >
      {{ title }}
    </h2>
    <CardList>
      <template #header v-if="hasHeader">
        <div class="flex items-center justify-between">
          <h2 class="text-xs font-bold uppercase leading-none text-slate-500">
            {{ title }}
          </h2>
          <IconButton
            v-if="createRoute"
            :icon="Plus"
            ariaLabel="Create"
            :href="createRoute"
            :variant="ButtonVariant.invisible"
            class="-my-4 -mr-3"
          />
        </div>
      </template>
      <EmptyState v-if="computedItems.length === 0" :title="`No ${title}`" class="mb-4 mt-5" />
      <div
        v-for="item in computedItems"
        :key="item.id"
        class="ring-inset hover:bg-slate-50 focus:outline-none focus:ring-3 focus:ring-primary-300 active:bg-slate-100"
      >
        <RouterLink :href="getRoute(item)">
          <div class="flex w-full shrink-0 items-center gap-x-3.5 px-4 py-3.5">
            <IconBadgeSquare :icon="icon" :color="getIconColor(item)" />
            <div class="flex flex-col md:gap-y-0.5">
              <div class="text-base font-semibold leading-5 text-slate-900">
                {{ getDisplayName(item) }}
              </div>
              <div class="flex items-center gap-x-1.5">
                <p class="text-sm text-slate-600">
                  {{ getDetail(item) }}
                </p>
              </div>
            </div>

            <EditAlt class="ml-auto h-4.5 w-4.5 shrink-0 text-slate-500" />
          </div>
          <div class="px-4 pb-4" v-if="isPendingDataRequest(item)">
            <Alert
              color="warning"
              :icon="Clock"
              variant="soft"
              title="Submission waiting for approval"
              size="sm"
            />
          </div>
        </RouterLink>
        <div class="px-4 pb-4" v-if="getUndismissedRejectedDataRequest(item)">
          <Alert
            color="danger"
            :icon="TimesCircle"
            title="Submission was not approved"
            :description="getUndismissedRejectedDataRequest(item)?.rejected_reason"
            variant="soft"
            alignment="vertical"
            hideClose
            size="sm"
            primaryButtonLabel="View and re-submit"
            secondaryButtonLabel="Dismiss"
            @onPrimaryClick="
              router.navigate({
                url: getCreateFromRejectedRoute(item)
              })
            "
            @onSecondaryClick="
              router.post(
                route('student.data-requests.dismiss-rejection', {
                  dataRequest: getUndismissedRejectedDataRequest(item)?.id
                })
              )
            "
          />
        </div>
      </div>
    </CardList>
  </div>
</template>
